/**
 * Cold outreach personalization (TRUS-772).
 *
 * For each prospect the backend researches the organization (what Lead
 * Builder knows plus a few pages of its own website), picks focus areas,
 * writes a first email and builds a landing page made for that prospect.
 * These pieces let a person set the campaign's mode, read the research,
 * edit and approve the draft, and send the follow-up after a response.
 */
import React, { useEffect, useState } from 'react';
import { CheckCircle2, ExternalLink, Loader2, RefreshCw, Send, Sparkles } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  coldOutreachApi,
  type ColdOutreachCampaign,
  type ColdOutreachProspect,
  type PersonalizationModels,
  type ProspectBrief,
} from '../../../shared/services/platformApi';

export function formatUsd(v?: number | null): string {
  if (v == null) return '';
  if (v === 0) return '$0';
  if (v < 0.01) return `<$0.01`;
  return `$${v.toFixed(v < 1 ? 3 : 2)}`;
}

function errorText(res: { error?: unknown }, fallback: string): string {
  const e = res.error as { message?: string } | string | undefined;
  if (typeof e === 'string') return e;
  return e?.message || fallback;
}

const STATUS_STYLE: Record<string, string> = {
  approved: 'bg-green-100 text-green-700',
  drafted: 'bg-amber-100 text-amber-800',
  researching: 'bg-blue-100 text-blue-700',
  pending: 'bg-gray-100 text-gray-600',
  failed: 'bg-red-100 text-red-700',
};

export function BriefStatusChip({ status }: { status?: string | null }) {
  const { t } = useAppTranslation();
  if (!status) return null;
  const label =
    {
      approved: t('platform.coldOutreach.brief.status.approved', 'Draft approved'),
      drafted: t('platform.coldOutreach.brief.status.drafted', 'Draft to review'),
      researching: t('platform.coldOutreach.brief.status.researching', 'Researching…'),
      pending: t('platform.coldOutreach.brief.status.pending', 'Queued'),
      failed: t('platform.coldOutreach.brief.status.failed', 'Research failed'),
    }[status] || status;
  return <span className={`px-2 py-0.5 text-[11px] font-medium rounded-full ${STATUS_STYLE[status] || ''}`}>{label}</span>;
}

/** Campaign-level personalization settings. */
export function CampaignPersonalizationSettings({
  campaign,
  onSaved,
}: {
  campaign: ColdOutreachCampaign;
  onSaved: () => void;
}) {
  const { t } = useAppTranslation();
  const [mode, setMode] = useState(campaign.personalization_mode || 'off');
  const [booking, setBooking] = useState(campaign.booking_url || '');
  const [offer, setOffer] = useState(campaign.offer_summary || '');
  const [model, setModel] = useState(campaign.personalization_model || '');
  const [models, setModels] = useState<PersonalizationModels | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    coldOutreachApi.getPersonalizationModels().then((res) => {
      if (res.success && res.data) setModels(res.data);
    });
  }, []);

  useEffect(() => {
    setMode(campaign.personalization_mode || 'off');
    setBooking(campaign.booking_url || '');
    setOffer(campaign.offer_summary || '');
    setModel(campaign.personalization_model || '');
  }, [campaign.id, campaign.personalization_mode, campaign.booking_url, campaign.offer_summary, campaign.personalization_model]);

  const save = async () => {
    setSaving(true);
    setMessage(null);
    const res = await coldOutreachApi.updateCampaign(campaign.id, {
      personalization_mode: mode,
      booking_url: booking.trim() || null,
      offer_summary: offer.trim() || null,
      personalization_model: model,
    });
    setSaving(false);
    if (res.success) {
      setMessage(t('platform.coldOutreach.personalization.saved', 'Saved.'));
      onSaved();
    } else {
      setMessage(errorText(res, 'Could not save. Try again.'));
    }
  };

  const researchAll = async () => {
    setMessage(null);
    const res = await coldOutreachApi.personalizeCampaign(campaign.id);
    setMessage(
      res.success && res.data
        ? t(
            'platform.coldOutreach.personalization.queued',
            'Researching {{count}} prospects in the background. Drafts appear on the Prospects tab as they finish.',
            { count: res.data.queued },
          )
        : errorText(res, 'Could not start research.'),
    );
  };

  return (
    <div className="bg-white rounded-lg shadow p-4 sm:p-6">
      <h3 className="font-semibold mb-1 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-blue-600" aria-hidden="true" />
        {t('platform.coldOutreach.personalization.title', 'Personalization')}
      </h3>
      <p className="text-xs text-gray-500 mb-4">
        {t(
          'platform.coldOutreach.personalization.help',
          "Researches each prospect's organization, writes a first email that references something specific, and gives them a page made for them.",
        )}
      </p>
      <div className="space-y-3 text-sm">
        <label className="block">
          <span className="text-gray-700 font-medium">{t('platform.coldOutreach.personalization.mode', 'First email')}</span>
          <select
            id="co-personalization-mode"
            value={mode}
            onChange={(e) => setMode(e.target.value as 'off' | 'review' | 'auto')}
            className="mt-1 block w-full border rounded-md px-2 py-1.5"
          >
            <option value="off">{t('platform.coldOutreach.personalization.modeOff', 'Use the template (no research)')}</option>
            <option value="review">
              {t('platform.coldOutreach.personalization.modeReview', 'Personalize, and wait for my approval')}
            </option>
            <option value="auto">{t('platform.coldOutreach.personalization.modeAuto', 'Personalize and send automatically')}</option>
          </select>
        </label>
        {mode !== 'off' && models && (
          <label className="block">
            <span className="text-gray-700 font-medium">{t('platform.coldOutreach.personalization.model', 'AI model')}</span>
            <select
              id="co-personalization-model"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="mt-1 block w-full border rounded-md px-2 py-1.5"
            >
              <option value="">
                {t('platform.coldOutreach.personalization.modelDefault', 'Platform default ({{model}})', {
                  model: models.models.find((m) => m.id === models.default_model)?.label || models.default_model,
                })}
              </option>
              {models.models.map((m) => (
                <option key={m.id} value={m.id}>
                  {t('platform.coldOutreach.personalization.modelOption', '{{label}} · {{tier}} · ~{{cost}} per prospect', {
                    label: m.label,
                    tier: m.tier,
                    cost: formatUsd(m.est_cost_per_prospect),
                  })}
                </option>
              ))}
            </select>
            <span className="block text-xs text-gray-500 mt-1">
              {models.source === 'env'
                ? t(
                    'platform.coldOutreach.personalization.modelEnv',
                    'The platform default is set on the server with CO_PERSONALIZATION_MODEL. Estimates use your past briefs when there are some.',
                  )
                : t(
                    'platform.coldOutreach.personalization.modelBuiltIn',
                    'Cheaper models write shorter, plainer research. Estimates use your past briefs when there are some.',
                  )}
              {models.env_valid === false &&
                ` ${t('platform.coldOutreach.personalization.modelEnvInvalid', 'CO_PERSONALIZATION_MODEL is set to an unknown model, so the built-in default is used.')}`}
            </span>
          </label>
        )}
        <label className="block">
          <span className="text-gray-700 font-medium">{t('platform.coldOutreach.personalization.offer', 'What you offer')}</span>
          <textarea
            id="co-offer-summary"
            value={offer}
            onChange={(e) => setOffer(e.target.value)}
            rows={3}
            placeholder={t(
              'platform.coldOutreach.personalization.offerPlaceholder',
              'e.g. WeaveIn helps CBOs run ECM and Community Supports: referrals, care coordination and Medi-Cal billing in one place.',
            )}
            className="mt-1 block w-full border rounded-md px-2 py-1.5"
          />
        </label>
        <label className="block">
          <span className="text-gray-700 font-medium">{t('platform.coldOutreach.personalization.booking', 'Booking link')}</span>
          <input
            id="co-booking-url"
            type="url"
            value={booking}
            onChange={(e) => setBooking(e.target.value)}
            placeholder="https://"
            className="mt-1 block w-full border rounded-md px-2 py-1.5"
          />
        </label>
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? t('platform.coldOutreach.personalization.saving', 'Saving…') : t('platform.coldOutreach.personalization.save', 'Save')}
          </button>
          {(campaign.personalization_mode || 'off') !== 'off' && (
            <button
              type="button"
              onClick={researchAll}
              className="px-3 py-1.5 border rounded-md text-sm hover:bg-gray-50"
            >
              {t('platform.coldOutreach.personalization.researchAll', 'Research all prospects')}
            </button>
          )}
          {message && <span className="text-xs text-gray-600">{message}</span>}
        </div>
      </div>
    </div>
  );
}

/** Research, draft, landing page and follow-up for one prospect. */
export function ProspectBriefDialog({
  campaignId,
  prospect,
  open,
  onClose,
  onChanged,
}: {
  campaignId: string;
  prospect: ColdOutreachProspect | null;
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t } = useAppTranslation();
  const [brief, setBrief] = useState<ProspectBrief | null>(null);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [fuSubject, setFuSubject] = useState('');
  const [fuBody, setFuBody] = useState('');

  const apply = (b: ProspectBrief | null) => {
    setBrief(b);
    setSubject(b?.draft_subject || '');
    setBody(b?.draft_body || '');
    setFuSubject(b?.followup_subject || '');
    setFuBody(b?.followup_body || '');
  };

  useEffect(() => {
    if (!open || !prospect) return;
    setLoading(true);
    setError(null);
    coldOutreachApi.getBrief(campaignId, prospect.id).then((res) => {
      setLoading(false);
      if (res.success && res.data) apply(res.data.brief);
      else setError(errorText(res, 'Could not load the brief.'));
    });
  }, [open, prospect, campaignId]);

  if (!prospect) return null;

  const run = async (key: string, fn: () => Promise<{ success: boolean; data?: unknown; error?: unknown }>) => {
    setWorking(key);
    setError(null);
    const res = await fn();
    setWorking(null);
    if (res.success) {
      const data = res.data as { brief?: ProspectBrief | null } | undefined;
      if (data && typeof data === 'object' && 'brief' in data) apply(data.brief || null);
      onChanged();
    } else {
      setError(errorText(res, 'Something went wrong. Try again.'));
    }
  };

  const generate = () => run('generate', () => coldOutreachApi.generateBrief(campaignId, prospect.id));
  const saveDraft = () =>
    run('save', () => coldOutreachApi.updateBrief(campaignId, prospect.id, { draft_subject: subject, draft_body: body }));
  const approve = () =>
    run('approve', async () => {
      const saved = await coldOutreachApi.updateBrief(campaignId, prospect.id, { draft_subject: subject, draft_body: body });
      if (!saved.success) return saved;
      return coldOutreachApi.approveBrief(campaignId, prospect.id);
    });
  const sendFollowup = () =>
    run('followup', async () => {
      const saved = await coldOutreachApi.updateBrief(campaignId, prospect.id, {
        followup_subject: fuSubject,
        followup_body: fuBody,
      });
      if (!saved.success) return saved;
      const sent = await coldOutreachApi.sendFollowup(campaignId, prospect.id);
      if (!sent.success) return sent;
      return coldOutreachApi.getBrief(campaignId, prospect.id);
    });

  const research = brief?.research || {};
  const busy = working !== null;
  const responseLabel: Record<string, string> = {
    book: t('platform.coldOutreach.brief.response.book', 'Asked to book a call'),
    question: t('platform.coldOutreach.brief.response.question', 'Sent a question'),
    not_now: t('platform.coldOutreach.brief.response.notNow', 'Said not now'),
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {prospect.first_name} {prospect.last_name}
            <span className="font-normal text-gray-500">· {prospect.company_name || prospect.email}</span>
            <BriefStatusChip status={brief?.status} />
          </DialogTitle>
          <DialogDescription>
            {t(
              'platform.coldOutreach.brief.description',
              'Research on their organization, the first email written from it, and the page made for them.',
            )}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="text-sm text-gray-500 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            {t('platform.coldOutreach.brief.loading', 'Loading…')}
          </p>
        ) : (
          <div className="space-y-5 text-sm">
            {error && <div className="p-3 bg-red-50 text-red-700 rounded-md">{error}</div>}
            {brief?.status === 'failed' && brief.error && (
              <div className="p-3 bg-red-50 text-red-700 rounded-md">{brief.error}</div>
            )}

            {(!brief || ['pending', 'failed'].includes(brief.status)) && (
              <div className="p-4 bg-gray-50 rounded-md flex flex-wrap items-center justify-between gap-3">
                <p className="text-gray-600">
                  {t(
                    'platform.coldOutreach.brief.none',
                    'No research yet. It takes about a minute: we read their website and what Lead Builder found, then write a draft.',
                  )}
                </p>
                <button
                  type="button"
                  onClick={generate}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {working === 'generate' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {working === 'generate'
                    ? t('platform.coldOutreach.brief.researching', 'Researching…')
                    : t('platform.coldOutreach.brief.research', 'Research and draft')}
                </button>
              </div>
            )}

            {brief && ['drafted', 'approved'].includes(brief.status) && (
              <>
                <section>
                  <h4 className="font-semibold text-gray-900 mb-1">{t('platform.coldOutreach.brief.summary', 'What we found')}</h4>
                  <p className="text-gray-700">{research.summary}</p>
                  {research.observations && research.observations.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {research.observations.map((o, i) => (
                        <li key={i} className="flex gap-2 text-gray-700">
                          <span className="text-gray-400">•</span>
                          <span>
                            {o.fact}{' '}
                            <a href={o.source_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-0.5">
                              {t('platform.coldOutreach.brief.source', 'source')}
                              <ExternalLink className="h-3 w-3" aria-hidden="true" />
                            </a>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                {research.focus_areas && research.focus_areas.length > 0 && (
                  <section>
                    <h4 className="font-semibold text-gray-900 mb-2">{t('platform.coldOutreach.brief.focus', 'Where we could help')}</h4>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {research.focus_areas.map((f, i) => (
                        <div key={i} className="border rounded-md p-3">
                          <p className="font-medium text-gray-900">{f.title}</p>
                          <p className="text-xs text-gray-600 mt-1">{f.why_it_matters}</p>
                          <p className="text-xs text-gray-500 mt-1">{f.how_we_help}</p>
                        </div>
                      ))}
                    </div>
                    {research.angle && (
                      <p className="text-xs text-gray-500 mt-2">
                        {t('platform.coldOutreach.brief.angle', 'Angle')}: {research.angle}
                      </p>
                    )}
                  </section>
                )}

                <section>
                  <h4 className="font-semibold text-gray-900 mb-2">{t('platform.coldOutreach.brief.draft', 'First email')}</h4>
                  <input
                    id="co-brief-subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="block w-full border rounded-md px-2 py-1.5 mb-2"
                    aria-label={t('platform.coldOutreach.brief.subject', 'Subject')}
                  />
                  <textarea
                    id="co-brief-body"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={9}
                    className="block w-full border rounded-md px-2 py-1.5 font-mono text-xs"
                    aria-label={t('platform.coldOutreach.brief.body', 'Body')}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t(
                      'platform.coldOutreach.brief.tokens',
                      '{{landing}} becomes their page link and {{sender}} your name. The unsubscribe link and postal address are added when it sends.',
                      { landing: '{{landing_url}}', sender: '{{sender_name}}' },
                    )}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <button
                      type="button"
                      onClick={approve}
                      disabled={busy}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                    >
                      <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                      {brief.status === 'approved'
                        ? t('platform.coldOutreach.brief.saveApproved', 'Save changes')
                        : t('platform.coldOutreach.brief.approve', 'Approve draft')}
                    </button>
                    {brief.status !== 'approved' && (
                      <button type="button" onClick={saveDraft} disabled={busy} className="px-3 py-1.5 border rounded-md hover:bg-gray-50 disabled:opacity-50">
                        {t('platform.coldOutreach.brief.save', 'Save without approving')}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={generate}
                      disabled={busy}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border rounded-md hover:bg-gray-50 disabled:opacity-50"
                    >
                      <RefreshCw className={`h-4 w-4 ${working === 'generate' ? 'animate-spin' : ''}`} aria-hidden="true" />
                      {t('platform.coldOutreach.brief.redo', 'Research again')}
                    </button>
                  </div>
                </section>

                <section className="border-t pt-4">
                  <h4 className="font-semibold text-gray-900 mb-1">{t('platform.coldOutreach.brief.page', 'Their page')}</h4>
                  <div className="flex flex-wrap items-center gap-3 text-gray-700">
                    {brief.landing_url && (
                      <a href={brief.landing_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">
                        {t('platform.coldOutreach.brief.preview', 'Open their page')}
                        <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                      </a>
                    )}
                    {brief.model && (
                      <span className="text-xs text-gray-400">
                        {t('platform.coldOutreach.brief.modelCost', 'Written by {{model}} · {{cost}}', {
                          model: brief.model,
                          cost: formatUsd(brief.cost_usd ?? 0),
                        })}
                      </span>
                    )}
                    <span className="text-xs text-gray-500">
                      {brief.landing_views > 0
                        ? t('platform.coldOutreach.brief.views', 'Viewed {{count}} times', { count: brief.landing_views })
                        : t('platform.coldOutreach.brief.notViewed', 'Not viewed yet')}
                    </span>
                    {brief.landing_response && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                        {responseLabel[brief.landing_response] || brief.landing_response}
                      </span>
                    )}
                  </div>
                </section>
              </>
            )}

            {brief?.followup_body && (
              <section className="border-t pt-4">
                <h4 className="font-semibold text-gray-900 mb-1">{t('platform.coldOutreach.brief.followup', 'Follow-up')}</h4>
                {brief.followup_reason && <p className="text-xs text-gray-500 mb-2">{brief.followup_reason}</p>}
                <input
                  id="co-followup-subject"
                  value={fuSubject}
                  onChange={(e) => setFuSubject(e.target.value)}
                  disabled={brief.followup_status === 'sent'}
                  className="block w-full border rounded-md px-2 py-1.5 mb-2"
                  aria-label={t('platform.coldOutreach.brief.followupSubject', 'Follow-up subject')}
                />
                <textarea
                  id="co-followup-body"
                  value={fuBody}
                  onChange={(e) => setFuBody(e.target.value)}
                  disabled={brief.followup_status === 'sent'}
                  rows={7}
                  className="block w-full border rounded-md px-2 py-1.5 font-mono text-xs"
                  aria-label={t('platform.coldOutreach.brief.followupBody', 'Follow-up body')}
                />
                {brief.followup_status === 'sent' ? (
                  <p className="text-xs text-green-700 mt-2">{t('platform.coldOutreach.brief.followupSent', 'Follow-up sent.')}</p>
                ) : (
                  <button
                    type="button"
                    onClick={sendFollowup}
                    disabled={busy}
                    className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Send className="h-4 w-4" aria-hidden="true" />
                    {working === 'followup'
                      ? t('platform.coldOutreach.brief.sending', 'Sending…')
                      : t('platform.coldOutreach.brief.sendFollowup', 'Send follow-up')}
                  </button>
                )}
              </section>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
